import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useTranslations } from '../hooks/useTranslations';
import { Toaster } from 'react-hot-toast';

import icon from '../assets/images/icon.svg';
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserGroupIcon, 
  ArrowLeftOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  BellIcon,
  UsersIcon,
  TicketIcon,
  HomeIcon
} from '@heroicons/react/24/outline';

function AdminLayout({ children }) {
  const { logout } = useAdminAuth();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { t } = useTranslations();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navigation = [
    { name: t('adminLayout.dashboard') || 'Главная', href: '/admin/dashboard', icon: HomeIcon },
    { name: t('adminLayout.users'), href: '/admin/users', icon: UsersIcon },
    { name: t('adminLayout.promoCode') || 'Промо-код', href: '/admin/promo-code', icon: TicketIcon },
    { name: t('adminLayout.settings'), href: '/admin/settings', icon: Cog6ToothIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#fff',
            color: '#1f2937',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 bg-white border-r border-gray-200 transition-all duration-300 ${isSidebarOpen ? 'w-64' : 'w-20'} shadow-sm`}>
        <div className="flex flex-col h-full">
          {/* Logo Header */}
          <div className="p-4 border-b border-gray-200">
            {isSidebarOpen ? (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-md">
                  <img src={icon} alt={t('adminLayout.iconAlt')} className="w-6 h-6" />
                </div>
                <div>
                  <h1 className="text-sm font-bold text-gray-900">{t('adminLayout.maxProfitAcademy')}</h1>
                  <p className="text-xs text-gray-500">{t('adminLayout.adminPanel')}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center shadow-md">
                  <img src={icon} alt={t('adminLayout.iconAlt')} className="w-6 h-6" />
                </div>
              </div>
            )}
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = window.location.pathname === item.href;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`group flex items-center ${isSidebarOpen ? 'px-3' : 'justify-center'} py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'bg-primary text-white shadow-lg shadow-primary/20' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                  }`}
                  title={!isSidebarOpen ? item.name : ''}
                >
                  <item.icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-900'}`} />
                  {isSidebarOpen && (
                    <span className="ml-3 font-medium">{item.name}</span>
                  )}
                  {isActive && isSidebarOpen && (
                    <div className="ml-auto w-2 h-2 rounded-full bg-white/30"></div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className={`group flex items-center w-full ${isSidebarOpen ? 'px-3' : 'justify-center'} py-3 text-sm font-medium text-gray-600 rounded-xl hover:bg-red-50 hover:text-red-600 transition-all duration-200`}
              title={!isSidebarOpen ? t('adminLayout.logout') : ''}
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5 flex-shrink-0 text-gray-500 group-hover:text-red-600" />
              {isSidebarOpen && <span className="ml-3">{t('adminLayout.logout')}</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-xl mr-4 text-gray-500 hover:bg-gray-100 transition-colors"
              >
                <Bars3Icon className="w-6 h-6" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{t('adminLayout.controlPanel')}</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  {window.location.pathname === '/admin/dashboard' && (t('adminLayout.dashboardSubtitle') || 'Обзор статистики')}
                  {window.location.pathname === '/admin/users' && (t('adminLayout.usersSubtitle') || 'Управление пользователями')}
                  {window.location.pathname === '/admin/promo-code' && (t('adminLayout.promoCodeSubtitle') || 'Управление промо-кодами')}
                  {window.location.pathname === '/admin/settings' && (t('adminLayout.settingsSubtitle') || 'Настройки системы')}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 bg-gray-50 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AdminLayout; 